import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, MessageSquare, Clock, Activity } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Panel, EmptyState, Card, Chip, inputCls } from './shared';
import { toast } from 'sonner';

export default function ApprovalsPanel() {
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [note, setNote] = useState({});

  const load = useCallback(() => {
    suiteAPI.listApprovals(filter === 'all' ? undefined : filter).then(r => setItems(r.data));
    suiteAPI.activityFeed().then(r => setActivity(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    await suiteAPI.decideApproval(id, { status, notes: note[id] || '' });
    toast.success(`Marked ${status.replace('_', ' ')}`);
    load();
  };

  return (
    <Panel title="Approvals & Activity" subtitle="Route creative through review before it goes out, and see everything the team touched." testId="suite-approvals">
      <div className="grid lg:grid-cols-[1fr_340px] gap-5">
        <div>
          <div className="flex gap-1.5 mb-4">
            {['pending', 'approved', 'changes_requested', 'rejected', 'all'].map(f => (
              <Chip key={f} active={filter === f} onClick={() => setFilter(f)} testId={`approval-filter-${f}`}>{f.replace(/_/g, ' ')}</Chip>
            ))}
          </div>
          {items.length === 0 ? <EmptyState icon={CheckCircle2} text="Nothing here. Send a design or campaign for approval from its panel." /> : (
            <div className="space-y-3">
              {items.map(a => (
                <Card key={a.id} className="p-4" data-testid={`approval-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{a.item_name}</p>
                      <p className="text-[11px] text-zinc-500 capitalize">{a.item_type} · requested {a.created_at?.slice(0, 10)}</p>
                      {a.review_notes && <p className="text-[11px] text-zinc-400 mt-1">Note: {a.review_notes}</p>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${
                      a.status === 'approved' ? 'bg-[#b8ff00]/15 text-[#b8ff00]' :
                      a.status === 'rejected' ? 'bg-red-500/15 text-red-400' :
                      a.status === 'changes_requested' ? 'bg-amber-500/15 text-amber-400' : 'bg-zinc-800 text-zinc-400'}`}>{a.status?.replace(/_/g, ' ')}</span>
                  </div>
                  {a.status === 'pending' && (
                    <div className="flex flex-wrap gap-2 mt-3 items-center">
                      <Input value={note[a.id] || ''} onChange={e => setNote({ ...note, [a.id]: e.target.value })} placeholder="Optional note…" className={`${inputCls} h-8 max-w-xs text-xs`} />
                      <Button size="sm" onClick={() => decide(a.id, 'approved')} className="bg-[#b8ff00] text-black font-bold text-xs" data-testid={`approve-${a.id}`}><CheckCircle2 size={12} className="mr-1" /> Approve</Button>
                      <Button size="sm" onClick={() => decide(a.id, 'changes_requested')} className="bg-zinc-800 text-white text-xs" data-testid={`changes-${a.id}`}><MessageSquare size={12} className="mr-1" /> Changes</Button>
                      <Button size="sm" onClick={() => decide(a.id, 'rejected')} className="bg-zinc-800 text-red-400 text-xs" data-testid={`reject-${a.id}`}><XCircle size={12} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>

        <Card className="p-4 h-fit">
          <p className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><Activity size={14} className="text-[#b8ff00]" /> Activity log</p>
          {activity.length === 0 ? <p className="text-xs text-zinc-500">No activity yet.</p> : activity.map(a => (
            <div key={a.id} className="py-2 border-b border-zinc-800 last:border-0">
              <p className="text-xs text-zinc-300"><span className="text-white">{a.actor_name}</span> {a.action?.replace(/_/g, ' ')}</p>
              <p className="text-[11px] text-zinc-500 truncate flex items-center gap-1"><Clock size={9} /> {a.created_at?.slice(0, 16).replace('T', ' ')} · {a.entity}</p>
            </div>
          ))}
        </Card>
      </div>
    </Panel>
  );
}
